import { FC, useEffect, useRef, useState } from 'react'
import mixpanel from 'mixpanel-browser'

import Layout from '../../common/components/Layout'
import MultipleSteps from '../../components/Exercise/MultipleSteps'
import {
  ExerciseData,
  ExerciseAnswer,
  ExerciseType,
} from '../../types/Exercise'
import InteractionBase from '../../common/components/InteractionBase'
import { authFetch } from '../../common/utils/cognito'
import ExerciseComponent from './MultipleSteps/ExerciseComponent'
import { useOnScreen } from '../../common/hooks/useOnScreen'

interface ExerciseProps {
  exercise: ExerciseData
}

const ExerciseContent: FC<ExerciseProps> = ({ exercise }) => {
  const isMultiStepsExercise = exercise.steps.length > 1
  const questionTitle = exercise.steps[0].header
  const context = exercise.steps[0].context
  const id = exercise.id

  const ref = useRef<HTMLDivElement>(null)
  const isOnScreen = useOnScreen(ref)
  const [exerciseAnswers, setExerciseAnswers] = useState<ExerciseAnswer>()
  const [isLoading, setIsLoading] = useState(true)

  const stepResponses = exerciseAnswers?.responses.stepResponses

  useEffect(() => {
    if (isOnScreen) {
      mixpanel.track('Exercise Loaded', {
        exerciseId: id,
      })
    }
  }, [id, isOnScreen])

  useEffect(() => {
    const fetchExercise = async () => {
      try {
        const response = await authFetch(
          `${process.env.REACT_APP_API_URL}/user-exercises/${id}`,
        )

        if (!response.ok) {
          throw new Error(response.statusText)
        }

        const responseBody = await response.text()

        if (responseBody) {
          const exerciseData = JSON.parse(responseBody)

          if (exerciseData.responses) {
            setExerciseAnswers({ ...exerciseData })
          }
        } else {
          const body = JSON.stringify({
            exerciseId: id,
            completed: false,
            responses: {
              stepResponses: [],
            },
          })

          await authFetch(`${process.env.REACT_APP_API_URL}/user-exercises`, {
            method: 'POST',
            headers: new Headers({
              'Content-Type': 'application/json; charset=utf-8',
            }),
            body: body,
          })
        }
      } catch (error) {
        console.error(error)
      }
      setIsLoading(false)
    }

    fetchExercise()
  }, [id])

  const onResponseSubmit = async (stepId: number, response: any) => {
    const newStepResponse = { stepId, response }

    mixpanel.track('Exercise Step Completed', {
      exerciseId: id,
      stepId,
    })

    const newStepResponses = [newStepResponse].reduce(
      (accumulator, currentValue) => {
        const existingAnswerIndex = accumulator.findIndex(
          ({ stepId }) => stepId === currentValue.stepId,
        )

        if (existingAnswerIndex === -1) {
          accumulator.push(currentValue)
        } else {
          accumulator[existingAnswerIndex] = currentValue
        }

        return accumulator
      },
      exerciseAnswers ? exerciseAnswers.responses.stepResponses : [],
    )

    setExerciseAnswers((prev) => ({
      ...prev,
      exerciseId: id,
      completed: exercise.steps?.length === newStepResponses.length,
      responses: {
        stepResponses: newStepResponses,
      },
    }))

    try {
      const isExerciseCompleted =
        exercise.steps?.length === newStepResponses.length

      if (isExerciseCompleted) {
        mixpanel.track('Exercise Completed', {
          exerciseId: id,
        })
      }

      const url = `${process.env.REACT_APP_API_URL}/user-exercises`
      const method = 'POST'
      const body = JSON.stringify({
        exerciseId: id,
        completed: isExerciseCompleted,
        responses: {
          stepResponses: newStepResponses,
        },
      })

      const fetchResponse = await authFetch(url, {
        method: method,
        headers: new Headers({
          'Content-Type': 'application/json; charset=utf-8',
        }),
        body: body,
      })

      if (!fetchResponse.ok) {
        throw new Error(await fetchResponse.text())
      }
    } catch (error) {
      console.error(error)
      throw error // re-throw error to catch it in handleSendClick
    }
  }

  if (isLoading) {
    // TODO: Replace with a loading screen
    return null
  }

  return (
    <Layout ref={ref}>
      {isMultiStepsExercise ? (
        <MultipleSteps
          exercises={exercise.steps}
          onResponseSubmit={onResponseSubmit}
          stepResponses={stepResponses}
        />
      ) : (
        <InteractionBase
          title={exercise.title}
          questionTitle={questionTitle}
          context={context}
          isExercise
          isSocialMedia={exercise.steps[0].type === ExerciseType.SOCIAL_MEDIA}
        >
          {!isLoading && (
            <ExerciseComponent
              exercise={exercise.steps[0]}
              onResponseSubmit={onResponseSubmit}
              stepResponses={stepResponses}
            />
          )}
        </InteractionBase>
      )}
    </Layout>
  )
}

export default ExerciseContent
